import type { AmbassadorCard as IAmbassadorCard } from '@bulknutrients/bulk-shared-lib/interfaces/Ambassador';
import { getAmbassadorsCardData } from '@bulknutrients/bulk-shared-lib/lib/sanity/ambassadors';
import { AmbassadorCard } from '@components/AmbassadorCard/AmbassadorCard';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Button, ButtonType } from '@components/Button';
import { Container } from '@components/Container';
import { Select } from '@components/FormComponents';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { StandardLayout } from '@components/Layouts';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import type { GetStaticProps } from 'next';
import Head from 'next/head';
import { type ReactElement, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

export default function CategoryPage({
  ambassadors,
  breadcrumbs,
  tags,
}: {
  ambassadors: IAmbassadorCard[];
  breadcrumbs: { title: string; path: string }[];
  tags: Record<string, string[]>;
}): ReactElement {
  const [displayedAmbassadors, setDisplayedAmbassadors] = useState(ambassadors);

  const rhfMethods = useForm<any>({
    mode: 'onTouched',
  });

  const filterAmbassadors = (value: Record<string, string>) => {
    const filtered = ambassadors.filter((a) => {
      let result = true;
      // Check all tags of ambassador for any to match the selected values
      for (const key of Object.keys(value)) {
        result =
          result &&
          a.tags.some(
            (tag) =>
              !value[key] ||
              value[key] === '' ||
              (tag.title === value[key] && tag.parent === key)
          );
      }
      return result;
    });

    setDisplayedAmbassadors(filtered);
  };

  useEffect(() => {
    const subscription = rhfMethods.watch((value) => {
      filterAmbassadors(value);
    });
    return () => subscription.unsubscribe();
  }, [rhfMethods.watch]);

  const onSubmit = (data, e) => {
    e.preventDefault();
  };

  return (
    <>
      <Head>
        <title>Recommended products from Bulk's Fitness Ambassadors</title>
        <meta
          name="title"
          content="Recommended products from Bulk's Fitness Ambassadors"
        />
        <meta
          name="description"
          content="Fitness Ambassadors from Bulk Nutrients give their meal plans, training plans and recommended products. Browse Bulk Nutrients' Ambassadors Recommendations"
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/categories/shop-by-recommended`}
        />
      </Head>
      <Container>
        <Breadcrumbs crumbs={breadcrumbs} />
      </Container>
      <main id="main-content">
        <Container
          as="article"
          className="grid-cols-[1fr_min(var(--width-max-width),calc(100%-16px))_1fr]"
        >
          <div className="max-w-(--width-narrow-page) justify-self-center">
            <h1>Shop by Recommended at Bulk Nutrients</h1>
            <Paragraph>
              No one person is the same - we all have different goals, different
              ways of training and different thoughts on nutrition. But we do
              share similarities with our friends, families and those we look up
              to. That is why we created this Shop by Recommended tool.
            </Paragraph>
            <Paragraph>
              It showcases all our Bulk Nutrients ambassadors with all the nitty
              gritty details on their lifestyle, training schedule and even how
              they eat each day.
            </Paragraph>
            <Paragraph>
              You can search by choosing your sport or interests, filter by
              female or male and can narrow down to see those with nutrition
              philosophies you're interested in. Shop by Recommended and find
              products best suited to your passions, goals, and lifestyle!
            </Paragraph>
            <Paragraph>
              Oh, and if you're looking to become a Bulk Nutrients Ambassador,
              check out our{' '}
              <TextLink href="/blog/feature-articles/tips-for-becoming-a-sponsored-ambassador">
                tips for becoming a sponsored athlete
              </TextLink>{' '}
              before{' '}
              <TextLink href="/contact-us/ambassador">
                completing our application form
              </TextLink>{' '}
              when you're ready 💪
            </Paragraph>
          </div>

          <HorizontalDivider />

          <FormProvider {...rhfMethods}>
            <div className="flex flex-col">
              <form
                onSubmit={rhfMethods.handleSubmit(onSubmit)}
                className="flex flex-wrap gap-4"
              >
                {Object.keys(tags).map((key) => (
                  <div className="m-0 flex gap-0" key={key}>
                    <Select
                      name={key}
                      labelClasses="!p-0"
                      label={`Browse by ${key}`}
                      options={tags[key].map((tag) => ({
                        value: tag,
                        label: tag,
                      }))}
                    />
                  </div>
                ))}
              </form>
              <Button
                type={ButtonType.button}
                onClick={() => {
                  setDisplayedAmbassadors(ambassadors);
                  rhfMethods.reset();
                }}
                buttonStyle="secondary"
                className="mb-4 w-fit"
              >
                Show All
              </Button>
            </div>

            {displayedAmbassadors.length === 0 ? (
              <div>
                Oh dear, we don't have any ambassadors matching those filters,
                try changing one or two of your selections 😀
              </div>
            ) : (
              <ul
                data-testid="ambassador-cards"
                className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3"
              >
                {displayedAmbassadors.map((ambassador, i) => (
                  <AmbassadorCard
                    ambassador={ambassador}
                    key={`ambassador${i}`}
                  />
                ))}
              </ul>
            )}
          </FormProvider>
        </Container>
      </main>
    </>
  );
}

CategoryPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const ambassadors = (await getAmbassadorsCardData()).sort((a, b) =>
    a.name > b.name ? 1 : -1
  );

  const tagsSet = ambassadors
    .flatMap((a) => a.tags)
    .reduce(
      (acc, cur) => {
        if (cur.parent) {
          if (Object.hasOwn(acc, cur.parent)) {
            acc[cur.parent].add(cur.title);
          } else {
            acc[cur.parent] = new Set([cur.title]);
          }
        }
        return acc;
      },
      {} as Record<string, Set<string>>
    );

  const tags = {} as Record<string, string[]>;
  Object.keys(tagsSet).forEach(
    (key) => (tags[key] = Array.from(tagsSet[key]).sort())
  );

  const breadcrumbs = [
    { title: 'home', path: '/' },
    { title: 'Shop by recommended', path: '/categories/shop-by-recommended' },
  ];

  return {
    props: {
      ambassadors,
      breadcrumbs,
      tags,
    },
  };
};
